<template>
    <div class="flex flex-col gap-y-0.5">
        <div
            v-for="route in menu"
            :key="route.id"
        >
            <div
                v-if="route.divider"
                class="pb-2 mt-2 border-t border-gray-100 dark:border-gray-760"
                :class="mini ? 'mx-2' : 'mx-4'"
            ></div>

            <CustomRouterLink
                class="px-4"
                :class="menuStyle(route)"
                :style="customStyle(route)"
                :route="route"
                :mini="mini"
                :simpleRote="!route.subMenu && !!route.routeName"
                @click.stop="onOpenSubMenu(route)"
                @mouseenter="onMouseenter(route, $event)"
                @mouseleave="onMouseleave"
            >
                <div
                    class="w-full flex items-center flex-nowrap gap-x-3 group relative"
                    :class="[route.isOpenSub && !mini && 'mb-1']"
                >
                    <div
                        class="w-5 "
                        :class="[
                            { 'dark:text-gray-200 ': !mini },
                            { '!text-purple dark:!text-purple-140': !route.isOpenSub && checkCurrentLink(route, route.subMenu) }
                        ]"
                    >
                        <component :is="route.icon()" />
                    </div>

                    <template v-if="!mini">
                        <div class="h-10 flex items-center gap-x-1">
                            <span class="text-center font-medium dark:font-normal text-xs whitespace-nowrap">
                                {{ route.name }}
                            </span>

                            <div
                                v-if="showAi(route.ai)"
                                class="min-w-6"
                            >
                                <AiIcon24x24 />
                            </div>
                        </div>

                        <div
                            v-if="route.subMenu"
                            class="flex items-center justify-center ml-auto"
                        >
                            <ArrowChevronBottomIcon
                                class="w-5 h-5 transform duration-100"
                                :class="{ 'rotate-180': route.isOpenSub }"
                            />
                        </div>
                    </template>

                    <div
                        v-if="getPendingCounter(route)"
                        class="flex items-center justify-center"
                        :class="mini ? 'w-2 h-2 top-0 right-1 absolute dark:bg-purple-140 bg-purple rounded-full' : 'ml-auto px-1 min-w-4 h-4 rounded-0.5 bg-purple-300/20'"
                    >
                        <span
                            v-if="!mini"
                            class="text-primary dark:text-purple-140 text-xs font-medium"
                        >
                            {{ getPendingCounter(route) }}
                        </span>
                    </div>
                </div>

                <RoutingSideBarMini
                    v-if="mini && isShowPopup === route.id"
                    :route="route"
                    :popupPosition="popupPosition"
                    :checkCurrentLink="checkCurrentLink"
                    @onMouseenter="mouseenter"
                />

                <RoutingSideBarBig
                    v-if="!mini"
                    :route="route"
                    :checkCurrentLink="checkCurrentLink"
                />
            </CustomRouterLink>
        </div>
    </div>
</template>

<script setup>
import {
    computed, ref, watch, inject, onMounted,
} from 'vue'
import { useRoute, useRouter } from 'vue-router'

import CustomRouterLink from '@/components/navigation/CustomRouterLink.vue'
import RoutingSideBarBig from '@/components/navigation/RoutingSideBar/RoutingSideBarBig.vue'
import RoutingSideBarMini from '@/components/navigation/RoutingSideBar/RoutingSideBarMini.vue'
import ArrowChevronBottomIcon from '@/components/icons/arrow/20x20/ArrowChevronBottomIcon.vue'
import AiIcon24x24 from '@/components/icons/AI/AiIcon24x24.vue'

import { showTranscription } from '@/helpers/call-transcription-whitelist-helper'

import { useRouterStore } from '@/store/routerStore'
import { useAuthorizationStore } from '@/store/authorizationStore'
import { usePermissionsStore } from '@/store/permissionsStore'
import { useDisputeCounterStore } from '@/store/call-tracking/dispute/disputeCounterStore'

import { CALL_MENU_ROUTES_BUYER, CALL_MENU_ROUTES_MANAGER, CALL_MENU_ROUTES_VENDOR } from '@/constants/callMenu'
import { NAVIGATION_ACTIONS } from '@/constants/navigationActions'
import { viewPorts } from '@/constants/viewPorts'
import { ROUTES } from '@/constants/routes'

const props = defineProps({
    mini: {
        type: Boolean,
        default: false,
    },
    showMobileSideBars: {
        type: Boolean,
        default: false,
    },
})

const routerStore = useRouterStore()
const authorizationStore = useAuthorizationStore()
const permissionsStore = usePermissionsStore()
const disputeCounterStore = useDisputeCounterStore()

const router = useRouter()
const route = useRoute()

const currentViewSize = inject('currentViewSize')

const menu = ref([])

const popupPosition = ref({})
const isShowPopup = ref(-1)

const timerId = ref(null)

const isMbView = computed(() => viewPorts.mb >= currentViewSize.value)

const isBuyer = computed(() => authorizationStore.isBuyer)
const isVendor = computed(() => authorizationStore.isVendor)

const viewTranscriptionPermission = computed(() => permissionsStore.permissions.transcription.view)

const pendingCounter = computed(() => disputeCounterStore.pendingCounter)

const getCallMenuByRole = computed(() => {
    switch (true) {
    case isVendor.value:
        return CALL_MENU_ROUTES_VENDOR
    case isBuyer.value:
        return CALL_MENU_ROUTES_BUYER
    default:
        return CALL_MENU_ROUTES_MANAGER
    }
})

onMounted(() => {
    menu.value = currentMenu()
})

function menuStyle(route) {
    const { isOpenSub, subMenu } = route

    const active = checkCurrentLink(route, subMenu)

    const classes = ['before:absolute before:right-0 before:top-0 before:w-0.75 before:h-0 before:bg-purple-140 transition-all cursor-pointer relative before:transition-all']

    if (!subMenu) {
        classes.push('flex')
    }

    if (isOpenSub && !props.mini) {
        classes.push(
            'dark:hover:bg-transparent text-gray-370 dark:text-white',
        )
    } else {
        classes.push(
            'hover:bg-menu-purple70 dark:hover:bg-menu-blue770',
            props.mini ? 'flex items-center justify-center' : '',
            active ? 'before:h-10 bg-menu-purple70 dark:bg-menu-blue770 border-purple dark:border-transparent text-purple dark:text-purple-140' : 'border-transparent dark:text-white text-gray-370 dark:hover:text-white',
        )
    }

    return classes.join(' ')
}

function getPendingCounter(route) {
    if (route.routeName === ROUTES.disputes && pendingCounter.value) {
        return pendingCounter.value
    }

    return null
}

function showAi(value) {
    return showTranscription() && viewTranscriptionPermission.value && value
}

function customStyle({ isOpenSub, subMenu }) {
    if (props.mini) {
        return { height: '40px' }
    }

    if (subMenu) {
        return { height: isOpenSub ? `${46 + (32 * subMenu.length)}px` : '40px' }
    }
}

function checkCurrentLink(parent, subMenu) {
    const { routeName, isFirstLoad } = parent

    if (subMenu) {
        const isActive = subMenu.some((el) => el.routeName === route.meta.reference)

        if (isActive && isFirstLoad) {
            parent.isOpenSub = isActive
            parent.isFirstLoad = false
        }

        return isActive
    }
    return route.meta.reference === routeName
}

function handleRouteMini({ routeName, paramsNames }) {
    if (!props.mini) return

    isShowPopup.value = -1
    router.push({ name: routeName, params: { ...routerStore.getParams(paramsNames) } })
}

function onOpenSubMenu(route) {
    const { isOpenSub, subMenu } = route

    if (subMenu) {
        route.isOpenSub = !isOpenSub
    }

    handleRouteMini(route)

    const foundElement = menu.value.find((el) => el.name !== route.name && el.isOpenSub)

    if (foundElement) {
        foundElement.isOpenSub = false
    }

    navigationActionHandler(route)
}

function navigationActionHandler(route) {
    if (!route.hasOwnProperty('action')) {
        return
    }

    switch (route.action) {
    case NAVIGATION_ACTIONS.logout:
        logout()
    }
}

function logout() {
    authorizationStore.logout()
}

function onMouseenter(route, event) {
    const { subMenu } = route
    if (!props.mini || !subMenu || isMbView.value) {
        return
    }
    openPopupSubMenu(route, event)
}

function openPopupSubMenu({ id }, { target }) {
    clearTimeout(timerId.value)
    isShowPopup.value = id

    const { left, top, width } = target.getBoundingClientRect()

    popupPosition.value = {
        position: 'fixed',
        left: `${left - (width / 2) + 85}px`,
        top: `${top}px`,
    }
}

function mouseenter(id) {
    clearTimeout(timerId.value)
    isShowPopup.value = id
}

function onMouseleave() {
    timerId.value = setTimeout(() => {
        isShowPopup.value = -1
    }, 100)
}

function currentMenu() {
    return permissionsStore.filterMenuByPermission(getCallMenuByRole.value)
}

function resetActiveMenu() {
    menu.value.forEach((el) => {
        if (el.isOpenSub) {
            el.isOpenSub = false
        }
    })
}

watch(route, () => {
    popupPosition.value = {}
})

watch(() => authorizationStore.workspace, () => {
    resetActiveMenu()
    menu.value = currentMenu()
})
</script>
